import { useState } from 'react'

import { navigateTo } from '../../../../../../common/frontend/route-utils.js'
import { t } from '../../../../../../common/i18n.js'
import type { Language } from '../../../../../../common/types/i18n.js'
import type {
    BalanceTimeCardPayload,
    BalanceTimePeriodSummary,
} from '../../../../../../server/controllers/portal/dashboard-card-payloads.js'
import { Button } from '../../../../../components/button/button.js'
import { HelpCircle } from '../../../../../components/icon/solar-icons.js'
import './balancetime-card.css'

const hours = (n: number): string => `${Math.round(n)}h`

type Period = 'month' | 'quarter'

/** Full, capitalized month name in the user's language (e.g. "Juuni" / "June"). */
const monthName = (date: Date, lang: Language): string => {
    const m = new Intl.DateTimeFormat(lang === 'et' ? 'et-EE' : 'en-US', {
        month: 'long',
    }).format(date)
    return m.charAt(0).toUpperCase() + m.slice(1)
}

const PeriodPanel = ({
    summary,
    period,
    lang,
}: {
    summary: BalanceTimePeriodSummary
    period: Period
    lang: Language
}) => {
    const tiles = t.portalHero.tiles
    const bt = t.portal.dashboard.cards.balancetime
    const pct =
        summary.expectedHours > 0
            ? Math.min(100, Math.round((summary.trackedHours / summary.expectedHours) * 100))
            : 0
    const over = summary.delta >= 0

    // Where you should be by today: tracked minus the running balance (delta).
    // The marker sits on the bar at that fraction of the full-period expectation;
    // fill past the marker = overtime, fill before it = behind.
    const expectedToDate = summary.trackedHours - summary.delta
    const markerPct =
        summary.expectedHours > 0
            ? Math.min(100, Math.max(0, Math.round((expectedToDate / summary.expectedHours) * 100)))
            : 0
    const deltaH = Math.round(summary.delta)
    const pace =
        deltaH > 0
            ? bt.paceAhead(deltaH)(lang)
            : deltaH < 0
              ? bt.paceBehind(-deltaH)(lang)
              : bt.paceOnTrack(lang)
    const markerTitle = `${bt.expectedToday(Math.round(expectedToDate))(lang)} · ${pace}`
    const saldoHint = over ? bt.saldoOver(lang) : bt.saldoUnder(lang)

    return (
        <>
            <div className="portal-balancetime__bar">
                <div className="portal-balancetime__bar-fill" style={{ width: `${pct}%` }} />
                {summary.expectedHours > 0 && (
                    <span
                        className="portal-balancetime__bar-marker"
                        style={{ left: `${markerPct}%` }}
                        title={markerTitle}
                        aria-label={markerTitle}
                    />
                )}
            </div>
            <div className="portal-card__stats">
                <div className="portal-card__stat">
                    <span className="portal-card__stat-label">{tiles.tracked(lang)}</span>
                    <span className="portal-card__stat-value">{hours(summary.trackedHours)}</span>
                </div>
                <div className="portal-card__stat">
                    <span className="portal-card__stat-label">{tiles.expectedToday(lang)}</span>
                    <span className="portal-card__stat-value portal-card__stat-value--muted">
                        {hours(expectedToDate)}
                    </span>
                </div>
                <div className="portal-card__stat">
                    <span className="portal-card__stat-label">
                        {period === 'quarter' ? tiles.expectedQuarter(lang) : tiles.expected(lang)}
                    </span>
                    <span className="portal-card__stat-value portal-card__stat-value--muted">
                        {hours(summary.expectedHours)}
                    </span>
                </div>
                <div className="portal-card__stat">
                    <span className="portal-card__stat-label portal-balancetime__saldo-label">
                        {tiles.overhours(lang)}
                        <span
                            className="portal-balancetime__saldo-info"
                            title={saldoHint}
                            aria-label={saldoHint}
                        >
                            <HelpCircle size={12} weight="Linear" />
                        </span>
                    </span>
                    <span
                        className="portal-card__stat-value"
                        style={{ color: over ? 'var(--portal-success)' : 'var(--portal-warning)' }}
                    >
                        {hours(summary.delta)}
                    </span>
                </div>
            </div>
        </>
    )
}

export const BalanceTimeCard = ({
    data,
    lang,
}: {
    data: BalanceTimeCardPayload
    lang: Language
}) => {
    const bt = t.portal.dashboard.cards.balancetime
    const [period, setPeriod] = useState<Period>('month')

    if (data.status === 'unavailable') {
        return <span className="portal-card__empty">{bt.unavailable(lang)}</span>
    }

    const summary = period === 'month' ? data.month : data.quarter

    // Live period labels for the dropdown — the current month name, and the
    // current quarter's month span (e.g. "Aprill–Juuni").
    const now = new Date()
    const q = Math.floor(now.getMonth() / 3) // 0..3
    const quarterMonths = `${monthName(new Date(now.getFullYear(), q * 3, 1), lang)}–${monthName(
        new Date(now.getFullYear(), q * 3 + 2, 1),
        lang,
    )}`

    return (
        <div className="portal-balancetime">
            <select
                className="portal-balancetime__select"
                value={period}
                onChange={(e) => setPeriod(e.target.value as Period)}
                aria-label={bt.periodMonth(lang) + ' / ' + bt.periodQuarter(lang)}
            >
                <option value="month">{bt.periodMonthCurrent(monthName(now, lang))(lang)}</option>
                <option value="quarter">{bt.periodQuarterCurrent(quarterMonths)(lang)}</option>
            </select>
            <PeriodPanel summary={summary} period={period} lang={lang} />
            <Button
                className="portal-balancetime__cta"
                variant="secondary"
                size="small"
                text={bt.viewLink(lang)}
                onClick={() => navigateTo('/me/balancetime')}
            />
        </div>
    )
}
